// The history with filters (FR-27 "nach Bohne und Zeitraum", concept beans-and-shot-history §3, page "Verlauf"): one
// filtered list with paging by the API's cursor and the key figures of the filter. Changes of a shot (taste, note,
// deletion) come from the main history store, which sees every HTTP answer and WebSocket delta (ShotsStore.onChange),
// with the same cloudRev rule (AD-44); a new shot reloads the first page.
import type { ShotItem, ShotSummaryResponse } from '@brewcore/app-contract';
import { startOfLocalDay, localDayOf } from './schedule.js';
import type { ShotChange, ShotsApi } from './shots.js';

/**
 * 'month' is the calendar's: the month of today from its 1st, never a choice of the list (Dominik 25.10.2026: "beim
 * kalender bei bezuegen soll oben der zeitraum als filter nicht angezeigt werden").
 */
export type Period = 'today' | '7d' | '30d' | 'all' | 'month';
export type TasteFilter = 'all' | 'sour' | 'good' | 'bitter' | 'unrated';

export interface ShotFilter {
  /** a bean of the library, 'none' for shots without a bean, null for all */
  bean: string | null;
  period: Period;
  taste: TasteFilter;
  /** a taste tag ID (catalog taste-tags), null for all */
  tag: string | null;
}

export const DEFAULT_FILTER: ShotFilter = { bean: null, period: '30d', taste: 'all', tag: null };

const PERIOD_DAYS: Record<Exclude<Period, 'all' | 'month'>, number> = { today: 0, '7d': 6, '30d': 29 };

/** The 1st of the month of a local day. */
const monthStart = (day: number): number => day - new Date(day * 86_400_000).getUTCDate() + 1;

/** The local day of the period's first day; null for "all". */
function firstDay(p: Period, now: number, zone?: string): number | null {
  if (p === 'all') return null;
  const day = localDayOf(now, zone);
  return p === 'month' ? monthStart(day) : day - PERIOD_DAYS[p];
}

const TASTE_CODE: Record<Exclude<TasteFilter, 'all'>, string> = {
  sour: '1',
  good: '2',
  bitter: '3',
  unrated: '0',
};

/** The query parameters of a filter; the period starts at local midnight (the browser's zone, as the day headings). */
export function filterParams(f: ShotFilter, now: number, zone?: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (f.bean) out.bean = f.bean;
  const first = firstDay(f.period, now, zone);
  if (first !== null) out.from = String(startOfLocalDay(first, zone));
  if (f.taste !== 'all') out.rating = TASTE_CODE[f.taste];
  if (f.tag) out.tag = f.tag;
  return out;
}

/** The same filter one period earlier (the days before the period's first one, the month before); null for "all". */
export function earlierParams(f: ShotFilter, now: number, zone?: string): Record<string, string> | null {
  const first = firstDay(f.period, now, zone);
  if (first === null || f.period === 'all') return null;
  const from = f.period === 'month' ? monthStart(first - 1) : first - PERIOD_DAYS[f.period] - 1;
  const out = filterParams({ ...f, period: 'all' }, now, zone);
  out.from = String(startOfLocalDay(from, zone));
  out.to = String(startOfLocalDay(first, zone));
  return out;
}

export interface ShotHistoryView {
  filter: ShotFilter;
  /** newest first */
  items: readonly ShotItem[];
  loaded: boolean;
  loading: boolean;
  next: string | null;
  summary: ShotSummaryResponse | null;
  /** how many of the filter's shots tasted good (null while unknown or when the filter is a taste) */
  good: number | null;
  /** how many shots the period before had (prototype "8 mehr als im September"); null for "all" */
  before: number | null;
  error: string | null;
}

/** New shots in a row become one reload of the first page. */
export const HISTORY_RELOAD_MS = 500;

export class ShotHistoryStore {
  private view: ShotHistoryView = {
    filter: DEFAULT_FILTER,
    items: [],
    loaded: false,
    loading: false,
    next: null,
    summary: null,
    good: null,
    before: null,
    error: null,
  };
  private readonly listeners = new Set<() => void>();
  private readonly revs = new Map<string, number>();
  private readonly deleted = new Set<string>();
  /** the filter of the request in flight: an answer for an older filter is dropped */
  private generation = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    readonly machineId: string,
    private readonly api: ShotsApi,
    private readonly now: () => number = Date.now,
  ) {}

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): ShotHistoryView => this.view;

  private commit(patch: Partial<ShotHistoryView>): void {
    this.view = { ...this.view, ...patch };
    for (const fn of this.listeners) fn();
  }

  /** Another filter: the list starts again from the newest shot. */
  setFilter(filter: ShotFilter): Promise<void> {
    this.commit({ filter, items: [], next: null, loaded: false, summary: null, good: null, before: null });
    return this.refresh();
  }

  async refresh(): Promise<void> {
    const gen = ++this.generation;
    const params = filterParams(this.view.filter, this.now());
    this.commit({ loading: true });
    const earlier = earlierParams(this.view.filter, this.now());
    const [list, summary, good, before] = await Promise.all([
      this.api.list(this.machineId, null, 20, params),
      this.api.summary(this.machineId, params),
      this.view.filter.taste === 'all'
        ? this.api.summary(this.machineId, { ...params, rating: TASTE_CODE.good })
        : Promise.resolve(null),
      earlier ? this.api.summary(this.machineId, earlier) : Promise.resolve(null),
    ]);
    if (gen !== this.generation) return;
    if (list.ok) {
      const items = list.value.items.filter((s) => !this.deleted.has(s.id)).map((s) => this.keep(s));
      this.commit({ items, next: list.value.next, loaded: true, error: null });
    } else this.commit({ error: list.error.code });
    if (summary.ok) this.commit({ summary: summary.value });
    this.commit({
      good: good?.ok ? good.value.count : null,
      before: before?.ok ? before.value.count : null,
      loading: false,
    });
  }

  async loadMore(): Promise<void> {
    if (!this.view.next || this.view.loading) return;
    const gen = this.generation;
    this.commit({ loading: true });
    const r = await this.api.list(
      this.machineId,
      this.view.next,
      20,
      filterParams(this.view.filter, this.now()),
    );
    if (gen !== this.generation) return;
    if (r.ok) {
      const more = r.value.items.filter((s) => !this.deleted.has(s.id)).map((s) => this.keep(s));
      const known = new Set(this.view.items.map((s) => s.id));
      this.commit({
        items: [...this.view.items, ...more.filter((s) => !known.has(s.id))],
        next: r.value.next,
      });
    } else this.commit({ error: r.error.code });
    this.commit({ loading: false });
  }

  /** The version the store knows if it is newer than s (by cloudRev); else s, and its revision is remembered. */
  private keep(s: ShotItem): ShotItem {
    const known = this.revs.get(s.id);
    if (known !== undefined && known > s.cloudRev) return this.view.items.find((x) => x.id === s.id) ?? s;
    this.revs.set(s.id, s.cloudRev);
    return s;
  }

  /** A change from the main store (taste, note, deletion). */
  apply(id: string, cloudRev: number, change: ShotChange): void {
    if (cloudRev <= (this.revs.get(id) ?? -1)) return;
    this.revs.set(id, cloudRev);
    if (change.deleted) {
      this.deleted.add(id);
      if (this.view.items.some((s) => s.id === id)) {
        this.commit({ items: this.view.items.filter((s) => s.id !== id) });
        void this.refreshSummary();
      }
      return;
    }
    if (!this.view.items.some((s) => s.id === id)) return;
    this.commit({
      items: this.view.items.map((s) =>
        s.id === id
          ? {
              ...s,
              ...('rating' in change ? { rating: change.rating ?? null } : {}),
              ...('note' in change ? { note: change.note ?? null } : {}),
              ...('tags' in change ? { tags: change.tags ?? [] } : {}),
              ...('tagsAuto' in change ? { tagsAuto: change.tagsAuto ?? [] } : {}),
              cloudRev,
            }
          : s,
      ),
    });
  }

  private async refreshSummary(): Promise<void> {
    const gen = this.generation;
    const r = await this.api.summary(this.machineId, filterParams(this.view.filter, this.now()));
    if (gen === this.generation && r.ok) this.commit({ summary: r.value });
  }

  /** A new shot arrived: the first page and the figures again (the filter decides whether it belongs). */
  onShot(): void {
    if (!this.view.loaded || this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.refresh();
    }, HISTORY_RELOAD_MS);
  }
}
